<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :title="$t('common.parcels')"
    :width="'80%'"
    :height="'85%'"
    :wrapper-attr="{ class: 'parcel-selector-popup' }"
    @hiding="onClose"
  >
    <GridPanelHeader
      :grid-ref="$refs.parcelGrid"
      :show-add="false"
      :show-refresh="true"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
      :no-bottom-margin="true"
      @refresh="refreshParcelGrid"
    />
    <DxDataGrid
      ref="parcelGrid"
      :data-source="parcelsDataSurce"
      :show-borders="true"
      :column-auto-width="true"
      :remote-operations="true"
      :hover-state-enabled="true"
      :show-column-headers="true"
      :repaint-changes-only="true"
      :selected-row-keys="selectedParcelUuids"
      @selection-changed="onGridSelectionChanged($event)"
      @row-click="onRowClick"
      @editor-preparing="onEditorPreparing"
      :element-attr="gridAttributes"
    >
      <DxColumn dataField="name" :caption="$t('common.name')" :filter-operations="['contains']" :allowSorting="false" width="18%" />
      <DxColumn
        dataField="surface"
        :caption="$t('common.surface_ha')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="12%"
      />
      <DxColumn
        dataField="layer"
        :caption="$t('common.layer')"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="false"
        alignment="center"
        width="8%"
        :allowHeaderFiltering="false"
        :allowEditing="false"
        cell-template="layerCellTemplate"
      />

      <template #layerCellTemplate="{ data }">
        <LayerSvg :layer="data.data?.layer?.coordinates[0] ?? []" />
      </template>

      <DxColumn
        dataField="department_name"
        :caption="$t('common.unit')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="18%"
      />
      <DxColumn
        dataField="season_uuid"
        cell-template="seasonViewTemplate"
        :caption="$t('common.season')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="15%"
        :min-width="150"
        :calculateCellValue="getSeasonColumnText"
      >
        <DxLookup :data-source="seasons" value-expr="season_uuid" display-expr="name" />
      </DxColumn>
      <template #seasonViewTemplate="{ data }">
        <span>
          {{ getSeasonColumnText(data.data.season_uuid) }}
        </span>
      </template>
      <DxColumn
        dataField="culture"
        :caption="$t('common.culture')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="15%"
      />
      <DxColumn
        dataField="parcel_group_name"
        :caption="$t('works.parcels.group_name')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="15%"
      />
      <DxColumn
        dataField="is_active"
        cell-template="isActiveViewTemplate"
        :caption="$t('common.active')"
        data-type="number"
        :allow-filtering="true"
        :filter-operations="['=']"
        :allowSorting="false"
        alignment="center"
        width="10%"
        :calculateCellValue="getActiveStatusNameByRow"
      >
        <DxLookup :data-source="activeLookup" value-expr="value" display-expr="text" />
      </DxColumn>
      <template #isActiveViewTemplate="{ data }">
        <span>{{ getActiveStatusName(data?.data?.is_active) }}</span>
      </template>

      <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="always" selectAllMode="page" />
      <DxFilterRow :visible="true" />
      <DxGrouping :autoExpandAll="true" />
      <DxGroupPanel :visible="false" />
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
    </DxDataGrid>
    <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: auto; padding-top: 16px">
      <SmagriaButton :label="$t('common.continue')" customClass="primary col-lg-2 apply-btn" :action="onContinue" />
    </div>
  </DxPopup>
</template>

<script>
import { mapActions } from "vuex";
import {
  DxDataGrid,
  DxColumn,
  DxSelection,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxPaging,
  DxLookup,
  DxPager,
} from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import CustomStore from "devextreme/data/custom_store";
import LayerSvg from "@/components/Map/LayerSvg.vue";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import { errorMessage } from "@/helpers/common";

export default {
  name: "ParcelSelector",
  components: {
    DxDataGrid,
    DxColumn,
    DxPopup,
    DxSelection,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
    LayerSvg,
    DxLookup,
    DxPager,
    GridPanelHeader,
    SmagriaButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: () => true,
    },
    selectedParcelUuids: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
    lockContextFilters: {
      type: Boolean,
      default: false,
    },
    singleParcelDepartment: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid parcel_selector_grid",
      },
      selectedParcelKeys: [...this.selectedParcelUuids],
      parcelsDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };

          const filterData = { ...this.filterData };
          if (Object.keys(filterData).length > 0) {
            Object.assign(payload, filterData);
          }

          let season_found = false;
          let i = 0;
          // Check if the filter contains 'season_uuid'
          if (payload.filter && Array.isArray(payload.filter)) {
            // Find all indexes where the filter is for 'season_uuid'
            while (i < payload.filter.length) {
              if (payload.filter[i] && payload.filter[i] == "season_uuid") {
                season_found = true;
                const season_uuid = payload.filter[i + 2];
                // Remove this and the next two keys (total 3)
                payload.filter.splice(i, 3);
                payload.filter.push(["season_uuid", "=", season_uuid]);
                break; // Exit the loop if we found a season filter
              } else {
                i++;
              }
            }
          }

          if (!season_found) {
            if (payload.filter && Array.isArray(payload.filter)) {
              // Find all indexes where the filter is for 'season_uuid'
              i = 0;
              while (i < payload.filter.length) {
                if (Array.isArray(payload.filter[i]) && payload.filter[i][0] == "season_uuid") {
                  season_found = true;
                  break; // Exit the loop if we found a season filter
                } else {
                  i++;
                }
              }
            }
          }

          if (!season_found && !filterData.season_uuid) {
            console.error("No season filter found in the payload (parcel selector).");
            return dataSource; // If no season filter, return empty data source
          }

          if (!payload.filter) {
            payload.filter = [];
          }
          if (payload.filter.length > 0) {
            payload.filter.push("and");
          }
          const contextFilters = [];
          if (filterData.season_uuid) contextFilters.push(["season_uuid", "=", filterData.season_uuid]);
          if (filterData.department_uuid) contextFilters.push(["department_uuid", "=", filterData.department_uuid]);
          contextFilters.forEach((filter) => {
            if (payload.filter.length > 0) payload.filter.push("and");
            payload.filter.push(filter);
          });
          payload.filter.push(["is_group", "=", 0]);

          if (!this.hasIsActiveFilter(payload.filter)) {
            if (payload.filter.length > 0) {
              payload.filter.push("and");
            }
            payload.filter.push(["is_active", "=", 1]);
          }

          try {
            await this.getParcels(payload).then((response) => {
              let rows = Array.isArray(response?.data) ? response.data : [];
              dataSource.data = rows;
              dataSource.totalCount = response?.pagination?.total ?? 1;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching parcels:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
      seasons: [],
      biggest_year_uuid: null, // To store the biggest UUID based on year
      activeLookup: [
        { value: 1, text: this.$t("common.active") },
        { value: 0, text: this.$t("common.inactive") },
      ],
    };
  },
  computed: {},
  watch: {
    selectedParcelUuids: {
      deep: true,
      handler(newData) {
        this.selectedParcelKeys = [...newData];
        this.$refs.parcelGrid.instance.selectRows(newData, false); // Update the grid selection
        // If you want to update the grid's selected rows based on the new selectedParcelUuids
        // this.$refs.parcelGrid.instance.getSelectedRowsData().forEach((row) => {
        //   if (newVal.includes(row.uuid)) {
        //     this.$refs.parcelGrid.instance.selectRows([row.uuid], false);
        //   } else {
        //     this.$refs.parcelGrid.instance.deselectRows([row.uuid]);
        //   }
        // });
      },
    },
    filterData: {
      deep: true,
      handler() {
        if (this.$refs.parcelGrid?.instance) {
          this.applyContextFilterValues();
          this.$refs.parcelGrid.instance.refresh();
        }
      },
    },
  },
  methods: {
    ...mapActions({
      getSeasons: "seasons/getSeasons",
      getParcels: "works-parcels/getParcels",
    }),
    onClose() {
      this.$emit("close");
    },
    onContinue() {
      const selectedRows = this.$refs.parcelGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected parcels to the parent component
    },
    refreshParcelGrid() {
      return this.$refs.parcelGrid?.instance?.refresh();
    },
    onGridSelectionChanged(e) {
      if (!this.checkDepartmentRestrictionForSelection(e)) {
        return;
      }
      //this.$emit("update:selectedParcels", e.selectedRowKeys);
    },
    onEditorPreparing(e) {
      if (this.lockContextFilters && e.parentType === "filterRow" && ["department_name", "season_uuid"].includes(e.dataField)) {
        e.editorOptions.disabled = true;
      }
    },
    applyContextFilterValues() {
      const grid = this.$refs.parcelGrid?.instance;
      if (!grid || !this.lockContextFilters) return;

      grid.columnOption("department_name", "filterValue", this.filterData.department_name || null);
      grid.columnOption("season_uuid", "filterValue", this.filterData.season_uuid || null);
    },
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      if (!this.checkDepartmentRestrictionForSelection(e)) {
        return;
      }

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedParcelUuids = [...this.selectedParcelKeys];
      const selectedIndex = selectedParcelUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        selectedParcelUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        selectedParcelUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.selectedParcelKeys = selectedParcelUuids;
      this.$refs.parcelGrid.instance.selectRows(selectedParcelUuids, false);
    },
    // If singleParcelDepartment is true, only allow selection of parcels with the same department_uuid
    checkDepartmentRestrictionForSelection(e) {
      if (this.singleParcelDepartment && this.selectedParcelKeys.length > 0) {
        const selectedRows = this.$refs.parcelGrid.instance.getSelectedRowsData();
        const currentDepartment = selectedRows.length > 0 ? selectedRows[0]?.department?.uuid : null;
        const clickedDepartment = e.data?.department?.uuid;
        if (currentDepartment && clickedDepartment && currentDepartment !== clickedDepartment) {
          // Optionally show a warning/toast here
          console.warn("You can only select parcels from the same department.");
          errorMessage(this, this.$t("works.parcels.parcel_department_error_on_select"));
          return false; // Prevent selection
        }
      }

      return true; // Allow selection
    },
    hasIsActiveFilter(filter) {
      if (!Array.isArray(filter)) {
        return false;
      }
      return filter.some((item) => {
        if (Array.isArray(item)) {
          return item[0] === "is_active";
        }
        return item === "is_active";
      });
    },
    getActiveStatusName(value) {
      return this.activeLookup.find((item) => item.value == value)?.text ?? "";
    },
    getActiveStatusNameByRow(rowData) {
      return this.getActiveStatusName(rowData?.is_active);
    },
    getSeasonColumnText(season_uuid) {
      const season = this.seasons.find((s) => s.season_uuid === season_uuid);
      return season ? season.name : "-";
    },
  },
  mounted() {
    this.getSeasons({ limit: 1000 })
      .then((response) => {
        this.seasons = response.data.map((season) => ({
          season_uuid: season.uuid,
          name: season.name,
          year: season.year, // Assuming 'year' is a property in the season object
        }));

        // Find the biggest UUID (lexicographically)
        const maxYear = Math.max(...this.seasons.map((s) => s.year));
        this.biggest_year_uuid = this.seasons.find((s) => s.year == maxYear)?.season_uuid;

        this.$nextTick(() => {
          // If you want to filter the grid immediately after loading
          if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
            //this.$refs.parcelGrid.instance.filter([["season_uuid", "=", this.biggest_year_uuid]]);
            // Set the filter value for the column (shows in filter row UI)
            this.$refs.parcelGrid.instance.columnOption(
              "season_uuid",
              "filterValue",
              this.lockContextFilters ? this.filterData.season_uuid : this.filterData.season_uuid || this.biggest_year_uuid,
            );
            if (this.lockContextFilters) {
              this.applyContextFilterValues();
            }
            this.$refs.parcelGrid.instance.columnOption("is_active", "filterValue", 1);
          }
        });

        console.log("Seasons fetched successfully");
      })
      .catch((error) => {
        console.error("Error fetching seasons:", error);
      });
  },
};
</script>

<style lang="scss">
.parcel-selector-popup .dx-popup-content {
  padding-top: 0 !important;
}
</style>

<style lang="scss" scoped>
.parcel_selector_grid {
  height: calc(95vh - 290px) !important;
}
</style>
